// src/pages/AISearchPage.js

import React, { useState, useMemo } from 'react';
import { collection, query, getDocs } from 'firebase/firestore';
import DOMPurify from 'dompurify';
import ReactMarkdown from 'react-markdown';
import { db } from '../App';
import { appId } from '../config/firebaseConfig'; // Importação corrigida

export default function AISearchPage() {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchScope, setSearchScope] = useState('');
    const [result, setResult] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const formatContext = (data) => {
        let contextString = '';
        if (data.clients && data.clients.length > 0) {
            contextString += '# Clientes e Equipamentos\n\n';
            data.clients.forEach(client => {
                contextString += `## Cliente: ${client.nomeFantasia || client.name}\n`;
                if (client.equipment && client.equipment.length > 0) {
                    client.equipment.forEach(equip => {
                        contextString += `### Equipamento: ${equip.data.nome || equip.type}\n`;
                        contextString += `- Tipo: ${equip.type}\n`;
                        for (const [key, value] of Object.entries(equip.data)) {
                            if (key !== 'nome' && typeof value !== 'object') {
                                if (value && String(value).trim() !== '') {
                                    contextString += `- ${key}: ${value}\n`;
                                }
                            } else if (key === 'customFields' && Array.isArray(value)) {
                                value.forEach(field => {
                                    if(field.name && field.value) {
                                       contextString += `- ${field.name}: ${field.value}\n`;
                                    }
                                });
                            }
                        }
                        contextString += '\n';
                    });
                }
            });
        }

        if (data.wiki && data.wiki.length > 0) {
            contextString += '# Wiki\n\n';
            data.wiki.forEach(item => {
                contextString += `## Tópico da Wiki: ${item.name}\n`;
                if (item.articles && item.articles.length > 0) {
                    item.articles.forEach(article => {
                        contextString += `### Artigo: ${article.name}\n`;
                        const textContent = new DOMParser().parseFromString(article.content, "text/html").documentElement.textContent;
                        contextString += `${textContent}\n\n`;
                    });
                }
            });
        }
        return contextString;
    };


    const handleSearch = async () => {
        if (!searchScope) {
            setError("Por favor, selecione um escopo para a pesquisa (Contrato, Avulso ou Wiki).");
            return;
        }
        if (!searchQuery.trim()) {
            setError("Por favor, digite sua pergunta.");
            return;
        }
        
        setIsLoading(true);
        setResult('');
        setError('');

        const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
        if (!apiKey) {
            setError("A chave de API do Gemini não foi encontrada. Verifique se o ficheiro .env está configurado corretamente e reinicie a aplicação.");
            setIsLoading(false);
            return;
        }

        try {
            const allData = {
                clients: [],
                wiki: [],
            };

            if (searchScope === 'contracts' || searchScope === 'avulso') {
                const collectionName = searchScope === 'contracts' ? 'clients' : 'clients-avulsos';
                const clientQuery = query(collection(db, `/artifacts/${appId}/public/data/${collectionName}`));
                const clientSnapshot = await getDocs(clientQuery);

                for (const clientDoc of clientSnapshot.docs) {
                    const clientData = { id: clientDoc.id, ...clientDoc.data(), equipment: [] };
                    if (clientData.ativo === false) continue;
                    const equipmentQuery = query(collection(db, `/artifacts/${appId}/public/data/${collectionName}/${clientDoc.id}/equipment`));
                    const equipmentSnapshot = await getDocs(equipmentQuery);
                    equipmentSnapshot.forEach(equipDoc => {
                        clientData.equipment.push({ id: equipDoc.id, ...equipDoc.data() });
                    });
                    allData.clients.push(clientData);
                }
            } else if (searchScope === 'wiki') {
                const wikiQuery = query(collection(db, `/artifacts/${appId}/public/data/wiki-items`));
                const wikiSnapshot = await getDocs(wikiQuery);
                for (const itemDoc of wikiSnapshot.docs) {
                    const itemData = { id: itemDoc.id, ...itemDoc.data(), articles: [] };
                    const articlesQuery = query(collection(db, `/artifacts/${appId}/public/data/wiki-items/${itemDoc.id}/articles`));
                    const articlesSnapshot = await getDocs(articlesQuery);
                    articlesSnapshot.forEach(articleDoc => {
                        itemData.articles.push({ id: articleDoc.id, ...articleDoc.data() });
                    });
                    allData.wiki.push(itemData);
                }
            }
            
            const context = formatContext(allData);
            
            const scopeName = {
                contracts: 'Clientes Contrato',
                avulso: 'Clientes Avulsos',
                wiki: 'Wiki'
            }[searchScope];

            const fullPrompt = `Você é um assistente da Intranet Arcus. Responda à pergunta do utilizador baseando-se apenas e exclusivamente no contexto da seção "${scopeName}" fornecido abaixo. Seja direto e claro. Se a resposta não estiver no contexto, diga que não encontrou a informação.\n\nContexto:\n${context}\n\nPergunta: ${searchQuery}`;

            const chatHistory = [{ role: "user", parts: [{ text: fullPrompt }] }];
            const payload = { contents: chatHistory };
            const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;
            
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorBody = await response.text();
                throw new Error(`API Error: ${response.statusText}. Body: ${errorBody}`);
            }

            const apiResult = await response.json();
            
            if (apiResult.candidates && apiResult.candidates[0]?.content?.parts[0]?.text) {
                const textResult = apiResult.candidates[0].content.parts[0].text;
                setResult(textResult);
            } else {
                setError("Não foi possível obter uma resposta da IA. A resposta veio em um formato inesperado.");
            }

        } catch (err) {
            console.error("Erro na pesquisa com IA:", err);
            setError("Ocorreu um erro ao realizar a pesquisa. Verifique a consola para mais detalhes.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="w-full text-center pt-8 md:pt-12 px-4 sm:px-6 md:px-8 pb-8">
            <header className="mb-8">
                <h1 className="text-4xl sm:text-5xl font-bold text-cyan-400">Pesquisa com IA</h1>
                <p className="text-slate-400 mt-2">Selecione onde quer pesquisar e faça uma pergunta em linguagem natural.</p>
            </header>

            <div className="mb-6">
                <label className="block text-sm font-medium mb-3 text-slate-300 text-center">1. Selecione onde pesquisar:</label>
                <div className="flex flex-wrap gap-4 justify-center">
                    <button
                        onClick={() => setSearchScope('contracts')}
                        className={`px-6 py-3 rounded-lg font-bold transition-all duration-200 transform hover:scale-105 ${searchScope === 'contracts' ? 'bg-cyan-500 text-white shadow-lg' : 'bg-slate-700 hover:bg-slate-600 text-slate-300 shadow-md'}`}
                    >
                        Clientes Contrato
                    </button>
                    <button
                        onClick={() => setSearchScope('avulso')}
                        className={`px-6 py-3 rounded-lg font-bold transition-all duration-200 transform hover:scale-105 ${searchScope === 'avulso' ? 'bg-cyan-500 text-white shadow-lg' : 'bg-slate-700 hover:bg-slate-600 text-slate-300 shadow-md'}`}
                    >
                        Clientes Avulsos
                    </button>
                    <button
                        onClick={() => setSearchScope('wiki')}
                        className={`px-6 py-3 rounded-lg font-bold transition-all duration-200 transform hover:scale-105 ${searchScope === 'wiki' ? 'bg-cyan-500 text-white shadow-lg' : 'bg-slate-700 hover:bg-slate-600 text-slate-300 shadow-md'}`}
                    >
                        Wiki
                    </button>
                </div>
            </div>

            <div>
                 <label className="block text-sm font-medium mb-2 text-slate-300 text-center">2. Digite sua pergunta:</label>
                <div className="flex gap-2 max-w-3xl mx-auto">
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Ex: Qual a senha do wifi do cliente X?"
                        className="w-full bg-slate-700 border border-slate-600 rounded-md p-3 focus:ring-2 focus:ring-cyan-400 focus:outline-none"
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <button
                        onClick={handleSearch}
                        disabled={isLoading || !searchScope}
                        className="bg-cyan-500 hover:bg-cyan-600 text-white font-bold py-3 px-6 rounded-md transition-colors disabled:bg-slate-500 disabled:cursor-not-allowed"
                        title={!searchScope ? "Selecione um escopo de pesquisa primeiro" : "Pesquisar"}
                    >
                        {isLoading ? 'A pesquisar...' : 'Pesquisar'}
                    </button>
                </div>
            </div>


            <div className="mt-8 p-6 bg-slate-800 rounded-lg min-h-[200px] max-w-4xl mx-auto text-left">
                {isLoading && <p className="text-center text-slate-400">A processar a sua pergunta...</p>}
                {error && <p className="text-red-500 text-center">{error}</p>}
                {result && (
                    <div className="prose prose-invert max-w-none">
                        <ReactMarkdown>{result}</ReactMarkdown>
                    </div>
                )}
                {!isLoading && !result && !error && (
                    <p className="text-center text-slate-500">Selecione um escopo e faça uma pergunta para ver o resultado aqui.</p>
                )}
            </div>
        </div>
    );
}
